import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service'
import { Account, IAccount } from '@services/accounts.service';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs/operators';
import { IOrganisation, OrganisationDataService } from '@services/organisation-data.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { EditOrganisationComponent } from 'src/app/dialogs/edit-organisation/edit-organisation.component';
import { UploadFile } from '@services/files.service';

/**
 * AccountManageOrganisationsComponent
 */
@Component({
	selector: 'app-account-manage-organisations',
	templateUrl: './account-manage-organisations.component.html',
	styleUrls: ['./account-manage-organisations.component.scss']
})
export class AccountManageOrganisationsComponent implements OnInit, OnChanges, OnDestroy {

	/** Path to Organisation Images */
	public imagePathOrganisations(): string {
		return imagePath(this.accountGUID, imgTypes.organisations).toLowerCase()
	}

	/** Account GUID */
	@Input() accountGUID: string;
	/** My Accounts List */
	public myAccounts: IAccount[];
	/** Selected Account */
	public selectedAccount: IAccount;
	/** Current User */
	public currentUser: IWebAppUser;
	/** organisations List */
	private organisationsList: IOrganisation[];
	/** organisation DataSource */
	public organisationDataSource: MatTableDataSource<any>
	/** displayed Organisations */
	public displayedOrganisations: IOrganisation[];
	/** Table Paginator */
	@ViewChild(MatPaginator) paginator: MatPaginator;
	/** Table Sort */
	@ViewChild(MatSort, { static: false }) matSort: MatSort;
	/** organisations Table Columns */
	public organisationsTableColumns = ["Image", "Name", "Description", "Edit"]
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/**
	 * Default Constructor
	 * @param authenticationService authenticationService
	 * @param globalFunctions globalFunctions
	 * @param siteSubscriptions siteSubscriptions
	 * @param organisationDataService organisationDataService
	 * @param dialog dialog
	 */
	constructor(
		private authenticationService: AuthenticationService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		private organisationDataService: OrganisationDataService,
		public dialog: MatDialog
	) { }

	/**
	 * Subscribes to changes in the input values and updates the selected asset object
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (this.accountGUID != '') {
			this.subscribeAccounts(this.accountGUID)
		}
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		// Validate the user
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise),
			distinctUntilChanged()
		).subscribe({
			next: (currentUser) => {
				// Check we have a validated User
				if (currentUser && !this.currentUser) {
					this.currentUser = <IWebAppUser>currentUser
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
			//complete: () => { console.log("this.authenticationService.currentUser$: Complete"); }
		})
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Subscribe to the list of My accounts
	 */
	subscribeAccounts(accountGUID: String) {

		this.siteSubscriptions.myAccounts$.pipe(
			takeUntil(this.finalise),
			distinctUntilChanged()
		).subscribe({
			next: (myAccounts) => {
				if (myAccounts) {
					this.myAccounts = myAccounts;
					this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
					this.selectedAccount = <IAccount>this.myAccounts.find(({ GUID }) => GUID.toUpperCase() === accountGUID.toUpperCase())

					// Check we have an account
					if (this.selectedAccount) {
						this.accountGUID = this.selectedAccount.GUID;
						/** Get Organisations/Locations for the Account */
						this.getOrganisations(this.accountGUID)
					}
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.myAccounts$", false) },
			//complete: () => { console.log("this.siteSubscriptions.myAccounts$: Complete"); }
		})
	}

	/**
	 * Get the Organisation list for this Account
	 * @param accountGUID
	 */
	async getOrganisations(accountGUID: string) {
		try {
			this.organisationsList = [];
			this.organisationsList = await firstValueFrom(
				this.organisationDataService.getOrganisations$(accountGUID)
			);
		} catch (error: any) {
			if (error?.status !== 404) {
				this.globalFunctions.HandleError(error, "getOrganisations: Error retrieving organisations");
			}
		}
		finally {
			this.organisationDataSource = new MatTableDataSource(this.organisationsList)
			/** Make sure we default to alias in order */
			this.organisationDataSource.sort = this.matSort;
			/** Set the filtered list for mobile view */
			this.displayedOrganisations = this.organisationDataSource.filteredData
		}
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * AddOrganisation
	 */
	AddOrganisation() {
		// this.globalFunctions.DisplaySnackBar({ message: "Not Yet Implemented", action: "" })

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Display the dialog box
		const dialogRef = this.dialog.open(EditOrganisationComponent, {
			data: { "organisation": {} }
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			let newOrg: IOrganisation;
			newOrg = result["organisation"];
			this.organisationDataService.postOrganisation$(this.accountGUID, newOrg).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.getOrganisations(this.accountGUID)
					this.globalFunctions.DisplaySnackBar({
						message: "Site Created",
						type: SnackType.Info,
						duration: 1000
					});
				},
				error: (error) => { this.globalFunctions.HandleError(error, "asset-tiles Error: this.organisationDataService.patchOrganisation$", false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}

	/**
	 * Edit the Location details
	 * @param editLocation
	 */
	editOrganisation(editOrganisation: IOrganisation): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Location Information
		var _edit = { ...editOrganisation };

		/* Add the Account GUID to the edit object */
		_edit.AccountGUID = this.accountGUID;

		// Display the dialog box
		const dialogRef = this.dialog.open(EditOrganisationComponent, {
			data: { "organisation": _edit }
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result["organisation"]);
			let orgImage = <UploadFile>{}
			if (result["file"]) {
				orgImage = {
					"container": result["container"],
					"fileBlob": result["file"],
					"filename": result["filename"],
					"folderLocation": result["folderLocation"]
				}
			}
			this.organisationDataService.patchOrganisation$(this.accountGUID, _edit.Uid, _edit, orgImage).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					Object.assign(editOrganisation, _edit);
					this.getOrganisations(this.accountGUID)
					this.globalFunctions.DisplaySnackBar({
						message: "Location Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
				},
				error: (error) => { this.globalFunctions.HandleError(error, "asset-tiles Error: this.organisationDataService.patchOrganisation$", false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}

	/**
	 * deleteOrganisations
	 * @param row
	 */
	deleteOrganisations(row: IOrganisation) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.UserConfirm$("Are you sure you want to delete Site: " + row.Name + "?", undefined, undefined, undefined, "500px").subscribe(async OKResult => {
			if (OKResult) {
				this.organisationDataService.deleteOrganisation$(this.accountGUID, row).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						this.getOrganisations(this.accountGUID)
						this.globalFunctions.DisplaySnackBar({
							message: "Site Deleted",
							type: SnackType.Info,
							duration: 1000
						});
					},
					error: (error) => { this.globalFunctions.HandleError(error, "asset-tiles Error: this.organisationDataService.patchOrganisation$", false) },
					complete: () => { this.globalFunctions.displaySpinner(false) }
				});
			}
		});
	}

	/**
	 * Key up event from the search input on HTML
	 * @param event HTMLInputElement
	 */
	applyFilterSites(value: string) {
		try {
			const filterValue = value.toLowerCase();

			this.organisationDataSource.filterPredicate = (data: IOrganisation, filter: string) => {
				/** Build a string per row to filter and convert to lower case */
				const textToSearch = (data.Name + data.Description).toLowerCase() || '';
				return textToSearch.indexOf(filter) !== -1;
			};

			this.organisationDataSource.filter = filterValue.trim().toLowerCase();

			this.displayedOrganisations = this.organisationDataSource.filteredData

			if (this.organisationDataSource.filteredData.length == 0) {
				this.globalFunctions.DisplaySnackBar({
					message: "No matches for '" + filterValue.trim().toLowerCase() + "'",
					type: SnackType.Alert,
					duration: 1000
				})
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "Error filtering list") }
	}

}
